import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { StringHelper } from '@helpers/string-helper';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
    selector: 'app-multi-select',
    templateUrl: './multi-select.component.html',
    styleUrls: ['./multi-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MultiSelectComponent implements ControlValueAccessor {
    @Input() public disabled: boolean;
    @Input() public loading: boolean;
    @Input() public showToggleAll = true;
    @Input() public toggleAllLabel: string;
    @Input() public showAllText = true;
    @Input() public items: MultiSelectItem[];
    @Input() public placeholder: string;
    @Input() public limitDropDownWidth = false;
    @Input() public limitNumberofSelections?: number; //TODO: Remove limitNumberofSelections when an number range component is implemented
    @Input() public searchable = false;
    @Output() public selectChange: EventEmitter<MultiSelectId[]> = new EventEmitter<MultiSelectId[]>();

    public readonly faCheck = faCheck;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouched = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    public onChange = (value: MultiSelectId[]) => {};
    public value: MultiSelectId[];

    public registerOnChange(fn: (value: MultiSelectId[]) => MultiSelectId[]) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    public setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    public writeValue(value: MultiSelectId[]) {
        if (!this.disabled) {
            this.value = value;
        }
    }

    public selectionChanged(value: MultiSelectId[]) {
        if (!this.limitNumberofSelections || value.length <= this.limitNumberofSelections) {
            this.value = value;
            this.selectChange.emit(value);
            this.onChange(value);
        }
    }

    public search(term: string, item: MultiSelectItem) {
        return StringHelper.wordSearch(item.name, term);
    }

    public applyFilter(ref: NgSelectComponent, $event: Event) {
        const term = ($event.target as HTMLInputElement).value;
        ref.filter(term);
    }

    public toggleAll() {
        if ((this.value?.length ?? 0) === (this.items?.length ?? 0)) {
            this.value = [];
        } else {
            this.value = this.items.map((item) => item.id);
        }

        this.selectChange.emit(this.value);
        this.onChange(this.value);
    }

    public handleKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            this.toggleAll();
            event.preventDefault(); // Prevent scrolling when pressing Space
        }
    }
}
